﻿import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";

import useListController from "@/hooks/list/useListController";
import { useHistoriesLookups, useHistories } from "@/hooks/histories/useHistories";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useHistoryExport } from "@/hooks/histories/useHistoryExport";
import useFilterStore from "@/stores/useFilterStore";
import { SCREEN_ID_PURCHASE_HISTORY } from "@/constants/screenIds";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import { usePurchaseHistoryFilters } from "./hooks/usePurchaseHistoryFilters";
import { usePurchaseHistoryColumns } from "./hooks/usePurchaseHistoryColumns";

import PurchaseHistoryDetails from "./PurchaseHistoryDetails";

import { PURCHASE_FILTERS, PURCHASE_METADATA, PURCHASE_BASE_TABLE } from "./purchase-history.constants";
import { getHistoryQuickActions } from "../Home/histories-metadata";

const PurchaseHistory = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;
	const { canShow, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_PURCHASE_HISTORY);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const {
		isFilterOpen,
		filtersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: PURCHASE_FILTERS,
		baseTable: PURCHASE_BASE_TABLE,
	});

	const { data: lookups, isLoading: isLoadingLookups } = useHistoriesLookups({
		type: "purchase",
		estate_id,
		estate_parent_id,
		search,
		parameter,
		enabled: canLoadScreenData,
	});

	const { isReady } = usePurchaseHistoryFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
	});

	const historyFilters = useMemo(
		() => ({
			estate_parent_id,
			estate_id,
			search,
			parameter,
			...appliedFilterParams,
			page: tableState.page,
			per_page: tableState.per_page,
			order_by: tableState.order_by,
			order_dir: tableState.order_dir,
		}),
		[estate_parent_id, estate_id, search, parameter, appliedFilterParams, tableState.page, tableState.per_page, tableState.order_by, tableState.order_dir],
	);

	const { data, isLoading, isFetching } = useHistories(PURCHASE_METADATA.apiType, historyFilters, {
		enabled: canLoadScreenData && !isLoadingLookups && Boolean(lookups) && isReady,
	});

	const columns = usePurchaseHistoryColumns(tableState);

	const { handleExport, isAlreadyExporting } = useHistoryExport("purchase", appliedFilterParams);

	const quickActions = useMemo(() => {
		const actions = getHistoryQuickActions("compra");
		return actions.map((action) => ({
			...action,
			onClick: () => navigate(action.path),
		}));
	}, [navigate]);

	const exportButton = useMemo(
		() => [
			{
				key: "export",
				label: "Exportar",
				icon: Download,
				variant: "secondary",
				disabled: isAlreadyExporting,
				options: [
					{ key: "excel", label: "Excel General", onClick: () => handleExport("nuevo_excel") },
					{ key: "excel_detail", label: "Excel Detalle", onClick: () => handleExport("nuevo_excel_detalle") },
				],
			},
		],
		[handleExport, isAlreadyExporting],
	);

	const renderExpandedContent = (row) => {
		return <PurchaseHistoryDetails items={row.original.details || []} />;
	};

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<ListScaffold
			quickActions={quickActions}
			actionButtons={exportButton}
			subtitle={PURCHASE_METADATA.subtitle}
			filterTitle={`Filtrar ${PURCHASE_METADATA.title}`}
			isFilterOpen={isFilterOpen}
			filters={filtersState}
			onFilterChange={handleFilterChange}
			onApplyFilters={handleApplyFilters}
			onClearFilters={handleClearAllFilters}
			onClearOneFilter={handleClearOneFilter}
			onOpenFilter={openFilter}
			onCloseFilter={closeFilter}
			isFetching={isFetching}
			isLoading={isLoading || isLoadingLookups}
			data={data?.data || []}
			totalItems={data?.totalRecords || 0}
			totalPages={data?.totalPages || 0}
			toItem={data?.data?.length || 0}
			fromItem={data?.page === 1 ? 1 : (data?.page - 1) * tableState.per_page + 1}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100, 200]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
			compactHeaders={true}
			compactBody={true}
			useExpandableRows={true}
			renderExpandedContent={renderExpandedContent}
		/>
	);
};

export default PurchaseHistory;
